import React, { useState } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { 
    ShieldCheck, 
    Lock, 
    Eye, 
    FileText, 
    UserCheck, 
    Server, 
    Cookie, 
    Mail, 
    Phone, 
    MapPin, 
    ArrowLeft, 
    Clock, 
    Calendar, 
    ChevronRight,
    CheckCircle2
} from 'lucide-react';
import { Link } from 'react-router-dom';

function PoliticaPrivacidad() {
    const [activeSection, setActiveSection] = useState('responsable');

    const sections = [
        { id: 'responsable', title: '1. Responsable del Tratamiento', icon: <UserCheck size={18} /> },
        { id: 'datos', title: '2. Datos que Recopilamos', icon: <Eye size={18} /> },
        { id: 'finalidad', title: '3. Finalidad del Tratamiento', icon: <FileText size={18} /> },
        { id: 'seguridad', title: '4. Seguridad y Confidencialidad', icon: <Lock size={18} /> },
        { id: 'transferencia', title: '5. Transferencia a Terceros', icon: <Server size={18} /> },
        { id: 'derechos', title: '6. Tus Derechos (Acceso y Supresión)', icon: <ShieldCheck size={18} /> },
        { id: 'cookies', title: '7. Cookies y Tecnologías Web', icon: <Cookie size={18} /> },
        { id: 'modificaciones', title: '8. Modificaciones a la Política', icon: <Calendar size={18} /> },
        { id: 'contacto', title: '9. Canales de Contacto', icon: <Mail size={18} /> },
    ];

    const scrollToSection = (id) => {
        setActiveSection(id);
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <div className="legal-page-wrapper">
            {/* Cabecera Hero */}
            <section className="legal-hero-section">
                <div className="legal-hero-background">
                    <div className="legal-shape legal-shape-1"></div>
                    <div className="legal-shape legal-shape-2"></div>
                    <div className="legal-hero-grid"></div>
                </div>

                <Container className="legal-hero-container">
                    <div className="legal-breadcrumb">
                        <Link to="/" className="legal-back-link">
                            <ArrowLeft size={16} /> Volver al Inicio
                        </Link>
                    </div>

                    <div className="legal-hero-badge">
                        <ShieldCheck size={20} />
                        <span>Transparencia y Seguridad</span>
                    </div>

                    <h1 className="legal-hero-title">
                        Política de <span className="title-highlight">Privacidad</span>
                    </h1>

                    <p className="legal-hero-description">
                        En <strong>Salcedo Importaciones S.R.L.</strong> nos comprometemos a proteger la privacidad y los 
                        datos personales de nuestros clientes, distribuidores y visitantes web en cumplimiento de los más 
                        altos estándares de seguridad y la legislación vigente en Bolivia.
                    </p>

                    <div className="legal-meta-info">
                        <span className="meta-item">
                            <Calendar size={16} /> Última actualización: Septiembre 2026
                        </span>
                        <span className="meta-divider">•</span>
                        <span className="meta-item">
                            <Clock size={16} /> Tiempo estimado de lectura: 6 min
                        </span>
                    </div>
                </Container>
            </section>

            {/* Tarjetas resumen de compromisos */}
            <section className="legal-highlights-section">
                <Container>
                    <Row className="g-4">
                        <Col md={4}>
                            <div className="legal-highlight-card">
                                <div className="highlight-icon-box">
                                    <Lock size={24} />
                                </div>
                                <h4>Protección Total</h4>
                                <p>Tus datos son almacenados en entornos protegidos bajo estrictos protocolos de confidencialidad.</p>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="legal-highlight-card">
                                <div className="highlight-icon-box">
                                    <ShieldCheck size={24} />
                                </div>
                                <h4>No Comercialización</h4>
                                <p>No vendemos, alquilamos ni transferimos tu información a terceros para fines ajenos a nuestro servicio.</p>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="legal-highlight-card">
                                <div className="highlight-icon-box">
                                    <UserCheck size={24} />
                                </div>
                                <h4>Control en tus Manos</h4>
                                <p>Puedes solicitar la actualización o supresión de tus datos en cualquier momento vía correo electrónico.</p>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Contenido Principal con Sidebar de Navegación */}
            <section className="legal-content-section py-5">
                <Container>
                    <Row className="g-4">
                        {/* Sidebar / Tabla de Contenidos */}
                        <Col lg={4} className="d-none d-lg-block">
                            <div className="legal-sidebar-sticky">
                                <Card className="legal-toc-card">
                                    <Card.Header className="legal-toc-header">
                                        <FileText size={18} />
                                        <span>Índice de Contenido</span>
                                    </Card.Header>
                                    <Card.Body className="p-0">
                                        <ul className="legal-toc-list">
                                            {sections.map((sec) => (
                                                <li key={sec.id}>
                                                    <button
                                                        className={`legal-toc-btn ${activeSection === sec.id ? 'active' : ''}`}
                                                        onClick={() => scrollToSection(sec.id)}
                                                    >
                                                        <span className="btn-icon">{sec.icon}</span>
                                                        <span className="btn-text">{sec.title}</span>
                                                        <ChevronRight size={14} className="arrow" />
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </Card.Body>
                                </Card>

                                <Card className="legal-help-card mt-4">
                                    <Card.Body>
                                        <h5>¿Dudas sobre tus datos?</h5>
                                        <p>Escríbenos directamente a nuestro canal de soporte legal y atención al cliente.</p>
                                        <a href="mailto:ventas@salcedoimport.com" className="btn-legal-contact">
                                            <Mail size={16} /> Contactar a Soporte
                                        </a>
                                    </Card.Body>
                                </Card>
                            </div>
                        </Col>

                        {/* Texto Legal */}
                        <Col lg={8}>
                            <div className="legal-document-body">
                                
                                {/* Sección 1 */}
                                <div id="responsable" className="legal-block">
                                    <div className="legal-block-header">
                                        <UserCheck className="block-icon" size={24} />
                                        <h2>1. Identificación del Responsable del Tratamiento</h2>
                                    </div>
                                    <p>
                                        El titular y responsable del tratamiento de los datos personales recabados a través 
                                        del sitio web es <strong>SALCEDO IMPORTACIONES S.R.L.</strong> (en adelante, "la Empresa"), 
                                        con domicilio legal en:
                                    </p>
                                    <ul className="legal-info-list">
                                        <li><strong>Razón Social:</strong> Salcedo Importaciones S.R.L.</li>
                                        <li><strong>Domicilio Principal:</strong> Av. Villazón Km. 5 Sacaba, Cochabamba, Bolivia.</li>
                                        <li><strong>Actividad Comercial:</strong> Venta al por mayor de artículos de ferretería, fontanería y calefacción.</li>
                                        <li><strong>Correo Electrónico de Contacto:</strong> <a href="mailto:ventas@salcedoimport.com">ventas@salcedoimport.com</a></li>
                                        <li><strong>Teléfonos de Contacto:</strong> (4) 544825 / +591 75986800</li>
                                    </ul>
                                </div>

                                {/* Sección 2 */}
                                <div id="datos" className="legal-block">
                                    <div className="legal-block-header">
                                        <Eye className="block-icon" size={24} />
                                        <h2>2. Datos Personales que Recopilamos</h2>
                                    </div>
                                    <p>
                                        Recopilamos únicamente los datos necesarios para brindar una atención personalizada, 
                                        atender consultas y tramitar solicitudes comerciales o de distribución:
                                    </p>
                                    <div className="legal-sub-box">
                                        <h4>a) Datos de Contacto General:</h4>
                                        <p>Nombre completo, número de teléfono o celular, dirección de correo electrónico, departamento/ciudad y el mensaje o consulta remitida.</p>
                                    </div>
                                    <div className="legal-sub-box">
                                        <h4>b) Datos de Solicitud de Distribuidor:</h4>
                                        <p>Nombre del titular o representante legal, correo electrónico, teléfono de contacto, departamento, rubro/tipo de negocio, nombre o razón social de la empresa y detalles de la propuesta comercial.</p>
                                    </div>
                                    <div className="legal-sub-box">
                                        <h4>c) Datos de Navegación Técnica:</h4>
                                        <p>Dirección IP aproximada, tipo de navegador, páginas visitadas dentro del sitio y tiempo de permanencia, con fines analíticos y de optimización de rendimiento.</p>
                                    </div>
                                </div>

                                {/* Sección 3 */}
                                <div id="finalidad" className="legal-block">
                                    <div className="legal-block-header">
                                        <FileText className="block-icon" size={24} />
                                        <h2>3. Finalidad del Tratamiento de Datos</h2>
                                    </div>
                                    <p>
                                        Los datos personales proporcionados serán utilizados exclusivamente para los siguientes fines legítimos:
                                    </p>
                                    <ul className="legal-bullet-list">
                                        <li><CheckCircle2 size={16} /> <strong>Atención de Consultas:</strong> Responder a solicitudes de información, cotizaciones y asistencia técnica.</li>
                                        <li><CheckCircle2 size={16} /> <strong>Evaluación de Distribuidores:</strong> Analizar perfiles comerciales para la habilitación de clientes de distribución mayorista en Bolivia.</li>
                                        <li><CheckCircle2 size={16} /> <strong>Comunicaciones Comerciales:</strong> Remitir información sobre novedades de productos, catálogos y promociones oficiales de marcas representadas (Truper, Pretul, Foset, Volteck, Fiero, Hermex, Klintek), siempre que el usuario haya otorgado su consentimiento.</li>
                                        <li><CheckCircle2 size={16} /> <strong>Seguridad del Sitio:</strong> Prevenir fraudes, ataques informáticos y garantizar el óptimo funcionamiento de la plataforma.</li>
                                    </ul>
                                </div>

                                {/* Sección 4 */}
                                <div id="seguridad" className="legal-block">
                                    <div className="legal-block-header">
                                        <Lock className="block-icon" size={24} />
                                        <h2>4. Seguridad y Confidencialidad de la Información</h2>
                                    </div>
                                    <p>
                                        Salcedo Importaciones S.R.L. implementa medidas técnicas, organizativas y metodos de seguridad 
                                        adecuadas para proteger los datos contra pérdida accidental, alteración, acceso no autorizado 
                                        o divulgación ilícita:
                                    </p>
                                    <ul className="legal-bullet-list">
                                        <li>Cifrado de comunicaciones mediante protocolo seguro HTTPS / SSL.</li>
                                        <li>Controles de acceso restringido al personal autorizado y debidamente capacitado.</li>
                                        <li>Políticas internas de respaldo y almacenamiento seguro en servidores dedicados.</li>
                                    </ul>
                                </div>

                                {/* Sección 5 */}
                                <div id="transferencia" className="legal-block">
                                    <div className="legal-block-header">
                                        <Server className="block-icon" size={24} />
                                        <h2>5. Transferencia y No Divulgación a Terceros</h2>
                                    </div>
                                    <p>
                                        <strong>No vendemos ni alquilamos tus datos personales.</strong> Únicamente podrán tener 
                                        acceso a ellos los proveedores de servicios tecnológicos (ej. hosting, servicios de envío de correo electrónico) 
                                        que actúen como encargados del tratamiento bajo acuerdos de estricta confidencialidad, o cuando 
                                        sea requerido expresamente por mandamiento legal u orden judicial de autoridad boliviana competente.
                                    </p>
                                </div>

                                {/* Sección 6 */}
                                <div id="derechos" className="legal-block">
                                    <div className="legal-block-header">
                                        <ShieldCheck className="block-icon" size={24} />
                                        <h2>6. Tus Derechos (Acceso, Rectificación y Cancelación)</h2>
                                    </div>
                                    <p>
                                        Como titular de tus datos personales, tienes derecho a:
                                    </p>
                                    <ul className="legal-bullet-list">
                                        <li><strong>Acceso:</strong> Solicitar conocer qué información tuya conservamos en nuestras bases de datos.</li>
                                        <li><strong>Rectificación:</strong> Solicitar la corrección o actualización de datos inexactos o incompletos.</li>
                                        <li><strong>Cancelación / Supresión:</strong> Solicitar la eliminación definitiva de tus datos cuando hayan dejado de ser necesarios para los fines que fueron recopilados.</li>
                                        <li><strong>Oposición:</strong> Oponerte en cualquier momento a recibir correos promocionales o boletines comerciales.</li>
                                    </ul>
                                    <p>
                                        Para ejercer cualquiera de estos derechos, sólo debes enviar una solicitud formal por escrito 
                                        a <a href="mailto:ventas@salcedoimport.com">ventas@salcedoimport.com</a> indicando tu nombre, 
                                        teléfono y el derecho que deseas ejercer.
                                    </p>
                                </div>

                                {/* Sección 7 */}
                                <div id="cookies" className="legal-block">
                                    <div className="legal-block-header">
                                        <Cookie className="block-icon" size={24} />
                                        <h2>7. Uso de Cookies y Tecnologías Web</h2>
                                    </div>
                                    <p>
                                        Este sitio web puede utilizar cookies técnicas y analíticas para mejorar la experiencia de 
                                        usuario, recordar preferencias de navegación y recopilar estadísticas anónimas de tráfico. 
                                        Puedes deshabilitar o configurar el uso de cookies en cualquier momento desde las preferencias 
                                        de tu navegador web.
                                    </p>
                                </div>

                                {/* Sección 8 */}
                                <div id="modificaciones" className="legal-block">
                                    <div className="legal-block-header">
                                        <Calendar className="block-icon" size={24} />
                                        <h2>8. Modificaciones a la Presente Política</h2>
                                    </div>
                                    <p>
                                        Salcedo Importaciones S.R.L. se reserva el derecho de actualizar o modificar esta Política de 
                                        Privacidad en función de nuevas exigencias legales o mejoras en nuestros servicios. 
                                        Cualquier cambio sustancial será publicado oportunamente en esta misma página con la fecha 
                                        de revisión actualizada.
                                    </p>
                                </div>

                                {/* Sección 9 */}
                                <div id="contacto" className="legal-block legal-contact-block">
                                    <div className="legal-block-header">
                                        <Mail className="block-icon" size={24} />
                                        <h2>9. Canales de Contacto y Dudas</h2>
                                    </div>
                                    <p>
                                        Si tienes preguntas, dudas o sugerencias respecto a esta Política de Privacidad o el tratamiento 
                                        de tus datos personales, puedes contactar a nuestro equipo por cualquiera de los siguientes medios:
                                    </p>
                                    
                                    <div className="legal-contact-grid">
                                        <div className="contact-box">
                                            <Mail size={20} className="icon" />
                                            <div>
                                                <strong>Correo de Soporte:</strong>
                                                <p>ventas@salcedoimport.com</p>
                                            </div>
                                        </div>
                                        <div className="contact-box">
                                            <Phone size={20} className="icon" />
                                            <div>
                                                <strong>Líneas Telefónicas:</strong>
                                                <p>4-544825 | 4-544712 | 75986800</p>
                                            </div>
                                        </div>
                                        <div className="contact-box">
                                            <MapPin size={20} className="icon" />
                                            <div>
                                                <strong>Oficina Central:</strong>
                                                <p>Av. Villazón Km. 5 Sacaba, Cochabamba - Bolivia</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-4 text-center">
                                        <Link to="/contacto" className="btn-ir-contacto">
                                            Ir al Formulario de Contacto
                                        </Link>
                                    </div>
                                </div>

                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
}

export default PoliticaPrivacidad;
